import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { OVERLAP_STATUS_OPTIONS } from "common/constants/overlap-status";
import { extractFieldErrors } from "common/lib/fieldErrors";
import { updateOverlappingPayment } from "common/services/payments.service";
import { useFormik } from "formik";
import * as moment from "moment";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Col,
  Form,
  FormFeedback,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import * as Yup from "yup";

const UpdatePayment: React.FC<{
  toggle: () => void;
  isModalOpen: boolean;
  fetchData: () => void;
  selectedRecord: any;
}> = ({ toggle, isModalOpen, fetchData, selectedRecord }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [, setFieldErrors] = useState<any>([]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      comments: selectedRecord?.comments || "",
      overlapStatus: selectedRecord?.overlapStatus || "",
      isResolvedOverlap: selectedRecord?.resolved,
    },
    validationSchema: Yup.object({
      comments: Yup.string(),
      // Legacy records predate the status, so only force a choice when the
      // record is actually being marked resolved.
      overlapStatus: Yup.string()
        .oneOf(
          ["", ...OVERLAP_STATUS_OPTIONS.map((option) => option.value)],
          "Please select a valid status"
        )
        .when("isResolvedOverlap", {
          is: true,
          then: (schema) => schema.required("Please select a status"),
        }),
    }),
    onSubmit: async (values: any) => {
      const data = {
        data: {
          comments: values.comments,
          overlapStatus: values.overlapStatus || null,
          resolved: values.isResolvedOverlap,
        },
      };

      setLoading(true);
      try {
        await updateOverlappingPayment(selectedRecord?.id, data);
        fetchData();
        validation.resetForm();
        toggle();
        toast.success("Payment updated successfully", {});
      } catch (e: any) {
        const errors = extractFieldErrors(e?.response);
        setFieldErrors(errors);
        toast.error("Payment update failed", {});
      } finally {
        setLoading(false);
      }
    },
  });

  // Update fieldErrors when validation errors change
  useEffect(() => {
    const validationErrors = [];

    // Convert Formik validation errors to fieldErrors format
    for (const [fieldName, errorMessage] of Object.entries(validation.errors)) {
      if (validation.touched[fieldName] && errorMessage) {
        // Handle nested object errors (like individual.value, mda.value, etc.)
        if (typeof errorMessage === "object" && errorMessage !== null) {
          for (const [nestedField, nestedError] of Object.entries(
            errorMessage
          )) {
            if (nestedError) {
              validationErrors.push({
                field: `${fieldName}.${nestedField}`,
                message: nestedError,
              });
            }
          }
        } else {
          validationErrors.push({
            field: fieldName,
            message: errorMessage,
          });
        }
      }
    }

    setFieldErrors(validationErrors);
  }, [validation.errors, validation.touched]);

  return (
    <Modal
      id="showModal"
      size="xl"
      isOpen={isModalOpen}
      toggle={toggle}
      centered
    >
      <ModalHeader className="bg-light p-3" toggle={toggle}>
        Edit Resolution
      </ModalHeader>

      <Form onSubmit={validation.handleSubmit}>
        <ModalBody>
          <Row className="mt-3 mb-4">
            <Col sm={6}>
              <h6 className="text-muted text-uppercase fw-semibold mb-3">
                First Payment Details
              </h6>

              <div
                className="table-responsive border border-dashed rounded p-2  mb-3"
                // style={{ minHeight: "200px" }}
              >
                <table className="table table-md table-borderless table-striped mb-0">
                  <tbody>
                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        MDA & Region
                      </td>
                      <td>
                        <p className="fw-medium mb-0 fs-14" id="billing-name">
                          {`${selectedRecord?.payment?.mda?.name} - ${selectedRecord?.payment?.mda?.region?.name}` ||
                            "Not Provided"}
                        </p>
                      </td>
                    </tr>
                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Agency:
                      </td>
                      <td>
                        {selectedRecord?.payment?.agency?.name ||
                          "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Individual Name:</td>
                      <td>
                        {selectedRecord?.payment?.individual?.fullName ||
                          "Not Provided"}
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Position Title:
                      </td>
                      <td>
                        {selectedRecord?.payment?.positionTitle ||
                          "Not Provided"}
                      </td>
                    </tr>

                    <tr>
                      <td className="fw-medium text-muted">Start Date:</td>
                      <td>
                        {selectedRecord?.payment?.startDate &&
                          moment(
                            new Date(selectedRecord?.payment?.startDate)
                          ).format("DD MMM Y")}
                      </td>
                    </tr>

                    <tr>
                      <td className="fw-medium text-muted">End Date:</td>
                      <td>
                        {selectedRecord?.payment?.endDate &&
                          moment(
                            new Date(selectedRecord?.payment?.endDate)
                          ).format("DD MMM Y")}
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Amount / Month:
                      </td>
                      <td>
                        {(selectedRecord?.payment?.amountPerMonth &&
                          "$" +
                            selectedRecord?.payment?.amountPerMonth?.toLocaleString()) ||
                          "Not Provided"}
                      </td>
                    </tr>

                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "25%" }}
                      >
                        Created At:
                      </td>
                      <td>
                        {moment(
                          new Date(selectedRecord?.payment?.createdAt)
                        ).format("DD MMM Y") || "Not Provided"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>
            <Col sm={6}>
              <h6 className="text-muted text-uppercase fw-semibold mb-3">
                Overlapping Payment Details
              </h6>

              <div className="table-responsive border border-dashed rounded p-2  mb-3 ">
                <table className="table table-md table-borderless table-striped mb-0">
                  <tbody>
                    <tr>
                      <td
                        className="fw-medium text-muted"
                        style={{ whiteSpace: "nowrap", width: "30%" }}
                      >
                        {" "}
                        MDA & Region
                      </td>
                      <td>
                        {`${selectedRecord?.overlappingPayment?.mda?.name} - ${selectedRecord?.overlappingPayment?.mda?.region?.name}` ||
                          "Not Provided"}
                      </td>
                    </tr>

                    <tr>
                      <td className="fw-medium text-muted">Agency:</td>
                      <td>
                        {selectedRecord?.overlappingPayment?.agency?.name ||
                          "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Individual Name:</td>
                      <td>
                        {selectedRecord?.overlappingPayment?.individual
                          ?.fullName || "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Position Title:</td>
                      <td>
                        {selectedRecord?.overlappingPayment?.positionTitle ||
                          "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Start Date:</td>
                      <td>
                        {selectedRecord?.overlappingPayment?.startDate &&
                          moment(
                            new Date(
                              selectedRecord?.overlappingPayment?.startDate
                            )
                          ).format("DD MMM Y")}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">End Date:</td>
                      <td>
                        {selectedRecord?.overlappingPayment?.endDate &&
                          moment(
                            new Date(
                              selectedRecord?.overlappingPayment?.endDate
                            )
                          ).format("DD MMM Y")}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Amount / Month:</td>
                      <td>
                        {(selectedRecord?.overlappingPayment?.amountPerMonth &&
                          "$" +
                            selectedRecord?.overlappingPayment?.amountPerMonth?.toLocaleString()) ||
                          "Not Provided"}
                      </td>
                    </tr>
                    <tr>
                      <td className="fw-medium text-muted">Created At:</td>
                      <td>
                        {moment(
                          new Date(
                            selectedRecord?.overlappingPayment?.createdAt
                          )
                        ).format("DD MMM Y") || "Not Provided"}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12} className="mb-4">
              <Label htmlFor="overlapStatus" className="form-label">
                What is this payment?
              </Label>
              <Input
                name="overlapStatus"
                id="overlapStatus"
                type="select"
                className="form-select"
                value={validation.values.overlapStatus}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                invalid={
                  validation.touched.overlapStatus &&
                  !!validation.errors.overlapStatus
                }
              >
                <option value="">Select a status</option>
                {OVERLAP_STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
              {validation.touched.overlapStatus &&
              validation.errors.overlapStatus ? (
                <FormFeedback type="invalid">
                  {/* @ts-ignore */}
                  {validation.errors.overlapStatus}
                </FormFeedback>
              ) : null}
            </Col>
            <Col md={12} className="mb-4">
              <div className="form-check form-switch form-switch-lg px-0 form-check-right">
                <Input
                  name="isResolvedOverlap"
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="isResolvedOverlap"
                  value={validation.values.isResolvedOverlap}
                  onChange={validation.handleChange}
                  checked={validation.values.isResolvedOverlap}
                />
                <Label
                  className="form-check-label"
                  htmlFor="isResolvedOverlap"
                  style={{ width: "250px" }}
                >
                  Is this a resolved overlapping payment?
                </Label>
              </div>
            </Col>
            <Col md={12} className="mb-3">
              <Label htmlFor="id-field" className="form-label">
                Comments on resolution (Optional)
              </Label>
              <CKEditor
                editor={ClassicEditor}
                data={validation.values.comments || ""}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  validation.setFieldValue("comments", data);
                }}
                onBlur={() => {
                  validation.handleBlur("comments");
                }}
              />
              {validation.touched.comments && validation.errors.comments ? (
                <FormFeedback type="invalid">
                  {/* @ts-ignore */}
                  {validation.errors?.comments}
                </FormFeedback>
              ) : null}
            </Col>
          </Row>
        </ModalBody>

        <div className="modal-footer">
          <div className="hstack gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-light"
              onClick={() => {
                toggle();
              }}
            >
              Close
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading && (
                <Spinner
                  color="light"
                  size="sm"
                  style={{ marginRight: "8px", marginBottom: "-1px" }}
                ></Spinner>
              )}
              Update Resolution
            </button>
          </div>
        </div>
      </Form>
    </Modal>
  );
};

export default UpdatePayment;
